import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  fontSize: 16px;
  fontWeight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #0056b3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin-right: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  
  &.edit {
    background: #007AFF;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 5px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #555;
  margin-bottom: 5px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #0056b3;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #545b62;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  .message {
    font-size: 18px;
    margin-bottom: 8px;
  }
  
  .submessage {
    font-size: 14px;
    opacity: 0.7;
  }
`;

const SearchBox = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;


const Residents = () => {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResident, setEditingResident] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    loadResidents();
  }, []);

  const loadResidents = async () => {
    try {
      const residentsData = await showingbookerAPI.getResidents();
      setResidents(residentsData);
    } catch (error) {
      console.error('Error loading residents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResident = () => {
    setEditingResident(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEditResident = (resident) => {
    setEditingResident(resident);
    setFormData({
      first_name: resident.first_name || '',
      last_name: resident.last_name || '',
      email: resident.email || '',
      phone: resident.phone || '',
      is_active: resident.is_active !== false
    });
    setShowModal(true);
  };

  const handleDeleteResident = async (residentId) => {
    if (window.confirm('Are you sure you want to delete this resident?')) {
      try {
        await showingbookerAPI.deleteResident(residentId);
        await loadResidents();
      } catch (error) {
        console.error('Error deleting resident:', error);
        alert('Failed to delete resident: ' + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData
      };

      if (editingResident) {
        await showingbookerAPI.updateResident(editingResident.id, submitData);
      } else {
        await showingbookerAPI.createResident(submitData);
      }
      setShowModal(false);
      await loadResidents();
    } catch (error) {
      console.error('Error saving resident:', error);
      alert('Failed to save resident: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  // Filter residents based on search term
  const filteredResidents = residents.filter(resident => {
    const searchLower = searchTerm.toLowerCase();
    const name = `${resident.first_name} ${resident.last_name}`;
    
    return name.toLowerCase().includes(searchLower) ||
           (resident.email && resident.email.toLowerCase().includes(searchLower)) ||
           (resident.phone && resident.phone.includes(searchTerm));
  });

  if (loading) {
    return <Container>Loading residents...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>🏠 Residents ({residents.length})</Title>
        <AddButton onClick={handleAddResident}>
          ➕ Add Resident
        </AddButton>
      </Header>

      {residents.length > 0 && (
        <SearchBox
          type="text"
          placeholder="🔍 Search residents by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}

      {residents.length === 0 ? (
        <EmptyState>
          <div className="icon">🏠</div>
          <div className="message">No residents added yet</div>
          <div className="submessage">Add property residents to reuse their information when creating properties</div>
        </EmptyState>
      ) : filteredResidents.length === 0 ? (
        <EmptyState>
          <div className="icon">🔍</div>
          <div className="message">No residents match your search</div>
          <div className="submessage">Try adjusting your search terms</div>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredResidents.map(resident => (
              <TableRow key={resident.id}>
                <TableCell>
                  {`${resident.first_name} ${resident.last_name}`}
                </TableCell>
                <TableCell>{resident.phone || '—'}</TableCell>
                <TableCell>{resident.email || '—'}</TableCell>
                <TableCell>
                  <ActionButton 
                    className="edit" 
                    onClick={() => handleEditResident(resident)}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton 
                    className="delete" 
                    onClick={() => handleDeleteResident(resident.id)}
                  >
                    Delete
                  </ActionButton>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add/Edit Resident Modal */}
      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingResident ? 'Edit Resident' : 'Add New Resident'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                ✕
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label>First Name *</Label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Last Name *</Label>
                  <Input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </FormGroup>
              </FormRow>


              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit">
                  {editingResident ? 'Update Resident' : 'Add Resident'}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Residents;